import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CurrencySelect } from "@/components/ui/currency-select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthProvider";
import { useCurrency } from "@/contexts/CurrencyContext";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2 } from "lucide-react";
import { handleSignOut } from "./ProfileMenu";

type UserRole = "cinematographer" | "gaffer" | "key_grip" | "producer" | "other";

type FormData = {
  full_name: string;
  company?: string;
  website?: string;
  role?: UserRole;
  default_currency?: string;
};

interface OnboardingFormProps {
  onComplete: () => void;
  isProfileComplete?: boolean;
}

export function OnboardingForm({ onComplete, isProfileComplete = false }: OnboardingFormProps) {
  const { user } = useAuth();
  const { setCurrency } = useCurrency();
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<string>("USD");
  
  const { register, handleSubmit, setValue, reset } = useForm<FormData>();

  // Fetch existing profile data if already completed
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user) return;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("full_name, company, website, role, default_currency")
        .eq("id", user.id)
        .single();
      
      if (error) {
        console.error("Error fetching profile data:", error);
        return;
      }
      
      if (data) {
        reset({
          full_name: data.full_name || "",
          company: data.company || "",
          website: data.website || "",
          role: data.role as UserRole || undefined,
          default_currency: data.default_currency || "USD"
        });
        
        // For the Select components which aren't directly handled by react-hook-form
        if (data.role) {
          setValue("role", data.role as UserRole);
        }
        if (data.default_currency) {
          setSelectedCurrency(data.default_currency);
          setValue("default_currency", data.default_currency);
        }
      }
    };
    
    fetchProfileData();
  }, [user, reset, setValue]);

  const onSubmit = async (data: FormData) => {
    if (!user) return;
    setIsLoading(true);

    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          ...data,
          default_currency: selectedCurrency,
          onboarding_completed: true,
        })
        .eq("id", user.id);

      if (error) throw error;
      
      // Update the currency context
      setCurrency(selectedCurrency);
      
      toast.success(isProfileComplete ? "Profile updated successfully!" : "Profile completed successfully!");
      onComplete();
    } catch (error: any) {
      toast.error("Error updating profile: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;
    setIsDeletingAccount(true);
    setDeleteError(null);

    try {
      // Insert a deletion request for this user
      const { error } = await supabase
        .from('deletion_requests')
        .insert({
          user_email: user.email,
        });

      if (error) {
        throw new Error(error.message || "Failed to submit deletion request");
      }
      setTimeout(() => handleSignOut(), 3000);
      toast.success("Your account deletion request has been submitted. You will be signed out shortly.");
    } catch (error: any) {
      console.error("Error requesting account deletion:", error);
      setDeleteError(error.message || "An unexpected error occurred");
      toast.error("Error requesting account deletion: " + error.message);
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const formTitle = isProfileComplete ? "Edit Your Profile" : "Complete Your Profile";
  const buttonText = isProfileComplete ? "Save Changes" : "Complete Profile";

  return (
    <div className="bg-background p-8 rounded-lg shadow-lg w-full max-w-md mx-auto">
      <h2 className="text-2xl font-bold mb-2">{formTitle}</h2>
      <p className="text-muted-foreground mb-6">
        {isProfileComplete 
          ? "Update your profile information" 
          : "Help us personalize your experience"}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="full_name">Full Name</Label>
          <Input
            id="full_name"
            {...register("full_name")}
            placeholder="John Doe"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="company">Company (Optional)</Label>
          <Input
            id="company"
            {...register("company")}
            placeholder="Company Name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Role</Label>
          <Select onValueChange={(value: UserRole) => setValue("role", value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select your role" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="producer">Producer</SelectItem>
              <SelectItem value="cinematographer">Cinematographer</SelectItem>
              <SelectItem value="gaffer">Gaffer</SelectItem>
              <SelectItem value="key_grip">Key Grip</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="default_currency">Default Currency</Label>
          <CurrencySelect
            value={selectedCurrency}
            onValueChange={(value) => {
              setSelectedCurrency(value);
              setValue("default_currency", value);
            }}
            placeholder="Select your default currency"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Website (Optional)</Label>
          <Input
            id="website"
            {...register("website")}
            placeholder="https://example.com"
          />
        </div>

        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {isProfileComplete ? "Saving..." : "Completing..."}
            </>
          ) : (
            buttonText
          )}
        </Button>
      </form>

      <div className="mt-8 pt-6 border-t">
        <h3 className="text-destructive font-semibold mb-4">Danger Zone</h3>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="w-full">
              Delete Account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your
                account and remove all your data from our servers.
                {deleteError && (
                  <p className="mt-2 text-destructive font-medium">
                    Error: {deleteError}
                  </p>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteAccount}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                disabled={isDeletingAccount}
              >
                {isDeletingAccount ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  "Delete Account"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
